import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Cropper from 'react-easy-crop';
import classNames from 'classnames';
import { DefaultButton } from '@fluentui/react/lib/Button';

const getOffsetFromCropSettings = (cropSettings) => {
  const { cropY, cropHeight } = cropSettings;

  if (!cropHeight) {
    return 0;
  }

  return -(100 * cropY) / cropHeight;
};

const convertPercentageOffsetToCropValue = ({
  offset,
  imageWidth,
  imageHeight,
  aspectRatio,
}) => {
  const cropAreaHeight = imageWidth * aspectRatio;
  const imageToCropAreaProportion = imageHeight / cropAreaHeight;
  const offsetNormalized = -(offset / 100) / imageToCropAreaProportion;
  const offsetInPixels = offsetNormalized * imageHeight;

  let y = 0;

  y += imageHeight / 2;
  y -= cropAreaHeight / 2;
  y -= offsetInPixels;

  return { x: 0, y };
};

export const ImageCropperEditor = (props) => {
  const {
    i18n,
    classes,
    src,
    cropSettings,
    aspectRatio,
    onCropFinished,
  } = props;

  const [crop, setCrop] = useState({ x: 0, y: 0 });

  const onMediaLoaded = useCallback(
    (mediaSize) => {
      const off = getOffsetFromCropSettings(cropSettings);
      const initialCrop = convertPercentageOffsetToCropValue({
        offset: off,
        imageWidth: mediaSize.width,
        imageHeight: mediaSize.height,
        aspectRatio,
      });
      setCrop(initialCrop);
    },
    [aspectRatio, cropSettings]
  );

  const onCropComplete = useCallback(
    (croppedArea) => {
      onCropFinished({
        cropX: 0,
        cropY: croppedArea.y / 100,
        cropWidth: 1,
        cropHeight: croppedArea.height / 100,
      });
    },
    [onCropFinished]
  );

  return (
    <div className="image-cropper-editor">
      <Cropper
        image={src}
        crop={crop}
        onCropChange={setCrop}
        onCropComplete={onCropComplete}
        onMediaLoaded={onMediaLoaded}
        zoom={1}
        zoomWithScroll={false}
        aspect={1 / aspectRatio}
        showGrid={false}
        objectFit="horizontal-cover"
        style={{
          cropAreaStyle: {
            border: '1px solid black',
            color: 'rgba(255, 255, 255, 0.65)',
          },
        }}
      />
      <div className={classNames(classes.overlay, classes.unclickable)}>
        <div className={
          classNames(
            classes.overlayStrip,
            classes.verticalStrip,
            classes.topStrip,
            classes.leftStrip
          )}
        />
        <div className={
          classNames(
            classes.overlayStrip,
            classes.verticalStrip,
            classes.middleStrip,
            classes.leftStrip
          )}
        />
        <div className={
          classNames(
            classes.overlayStrip,
            classes.verticalStrip,
            classes.bottomStrip,
            classes.leftStrip
          )}
        />

        <div className={
          classNames(
            classes.overlayStrip,
            classes.verticalStrip,
            classes.topStrip,
            classes.rightStrip
          )}
        />
        <div className={
          classNames(
            classes.overlayStrip,
            classes.verticalStrip,
            classes.middleStrip,
            classes.rightStrip
          )}
        />
        <div className={
          classNames(
            classes.overlayStrip,
            classes.verticalStrip,
            classes.bottomStrip,
            classes.rightStrip
          )}
        />

        <div className={
          classNames(
            classes.overlayStrip,
            classes.horizontalStrip,
            classes.topStrip,
            classes.leftStrip
          )}
        />
        <div className={
          classNames(
            classes.overlayStrip,
            classes.horizontalStrip,
            classes.topStrip,
            classes.rightStrip
          )}
        />

        <div className={
          classNames(
            classes.overlayStrip,
            classes.horizontalStrip,
            classes.bottomStrip,
            classes.leftStrip
          )}
        />
        <div className={
          classNames(
            classes.overlayStrip,
            classes.horizontalStrip,
            classes.bottomStrip,
            classes.rightStrip
          )}
        />

        <DefaultButton
          className={classes.overlayMessage}
          iconProps={{ iconName: 'Move' }}
        >
          {i18n.getString(_TL_('Drag to reposition'))}
        </DefaultButton>
      </div>
    </div>
  );
};

ImageCropperEditor.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    img: PropTypes.string.isRequired,
  }).isRequired,
  src: PropTypes.string.isRequired,
  cropSettings: PropTypes.shape({}).isRequired,
  aspectRatio: PropTypes.number.isRequired,
  onCropFinished: PropTypes.func.isRequired,
};
